import { Textarea } from '@/components/ui/textarea';
import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';

import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import {
  FocusEventHandler,
  forwardRef,
  KeyboardEventHandler,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { ClearPlugin, ClearPluginRef } from '@/components/editor/ClearPlugin';
import { FocusPlugin, FocusPluginRef } from '@/components/editor/FocusPlugin';
import { KeydownPlugin } from '@/components/editor/KeydownPlugin';
import { ListIndentPlugin } from '@/components/editor/ListIndentPlugin';
import { MarkdownExportPlugin } from '@/components/editor/MarkdownExportPlugin';
import { MarkdownImportPlugin } from '@/components/editor/MarkdownImportPlugin';
import { PastePlugin } from '@/components/editor/PastePlugin';
import { MentionTypeaheadPlugin } from './MentionPlugin/MentionTypeaheadPlugin';
import { TicketTypeaheadPlugin } from './TicketPlugin/TicketTypeaheadPlugin';
import { TicketListInsertProvider } from './TicketPlugin/TicketListInsertProvider';
import { InsertTicketData } from './TicketPlugin/types';
import { ToolbarPlugin } from '@/components/editor/ToolbarPlugin';
import { useEditorConfig } from '@/components/editor/useEditorConfig';
import { cn } from '@/lib/utils';

interface EditorProps {
  defaultValue?: string;
  namespace: string;
  placeholder?: string;
  onKeyDown?: KeyboardEventHandler;
  onBlur?: FocusEventHandler;
  disabled?: boolean;
  onChange: (value: string) => void;
  onPasteFiles?: (files: File[]) => void;
}

export interface EditorRef {
  clear: () => void;
  setValue: (value: string) => void;
  focus: () => void;
}

// TODO: style user autocomplete
// TODO: fix autocomplete dropdown to not go off-screen and follow the selection by scrolling
export const Editor = forwardRef<EditorRef, EditorProps>(
  (
    {
      defaultValue,
      onChange,
      namespace,
      placeholder = 'Type here...',
      disabled,
      onKeyDown,
      onBlur,
      onPasteFiles,
    },
    ref,
  ) => {
    const editorConfig = useEditorConfig(namespace);
    const [value, setValue] = useState(defaultValue ?? '');
    const [isMarkdownMode, setIsMarkdownMode] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const clearPluginRef = useRef<ClearPluginRef>(null);
    const focusPluginRef = useRef<FocusPluginRef>(null);
    const markdownTextareaRef = useRef<HTMLTextAreaElement>(null);
    const setValueRef = useRef<((value: string) => void) | null>(null);
    const insertTicketListRef = useRef<
      ((ticketsData: InsertTicketData | InsertTicketData[]) => void) | null
    >(null);

    const handleToggleMarkdownMode = useCallback(() => {
      setIsMarkdownMode((prev) => {
        const newMode = !prev;
        // Focus textarea when switching to markdown mode
        if (newMode) {
          setTimeout(() => {
            markdownTextareaRef.current?.focus();
          }, 0);
        }
        return newMode;
      });
    }, []);

    const handleChange = useCallback(
      (newValue: string) => {
        setValue(newValue);
        onChange(newValue);
      },
      [onChange],
    );

    const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      setIsDragOver(true);

      // Position cursor at mouse coordinates using browser APIs
      // This creates a text range at the exact mouse position and sets the selection there
      // Note: Firefox uses caretPositionFromPoint() instead of caretRangeFromPoint(),
      // but we only support Chromium-based browsers for now
      const range = document.caretRangeFromPoint?.(e.clientX, e.clientY);

      if (range) {
        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(range);
        }

        // Focus the editor to make the cursor visible
        const editorElement = e.currentTarget.querySelector(
          '[contenteditable="true"]',
        );
        if (editorElement instanceof HTMLElement) {
          editorElement.focus({ preventScroll: true });
        }
      }
    }, []);

    const handleDragLeave = useCallback(
      (e: React.DragEvent<HTMLDivElement>) => {
        // Only set to false if we're leaving the container, not a child element
        if (
          e.currentTarget === e.target ||
          !e.currentTarget.contains(e.relatedTarget as Node)
        ) {
          setIsDragOver(false);
        }
      },
      [],
    );

    const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragOver(false);

      // Check for multiple entities first
      const entitiesData = e.dataTransfer.getData('application/x-entities');
      if (entitiesData) {
        try {
          const entities = JSON.parse(entitiesData);
          if (Array.isArray(entities) && insertTicketListRef.current) {
            // Filter only tickets and insert as bullet list
            const tickets = entities
              .filter((entity) => entity.entityType === 'ticket')
              .map((entity) => ({
                id: entity.id,
                number: entity.number,
                title: entity.title,
                showTitle: true, // Always show title
              }));

            if (tickets.length > 0) {
              insertTicketListRef.current(tickets);
            }
          }
          return;
        } catch (error) {
          console.error('Failed to parse dropped entities:', error);
        }
      }

      // Fall back to single entity
      const entityData = e.dataTransfer.getData('application/x-entity');
      if (!entityData) return;

      try {
        const entity = JSON.parse(entityData);

        // Insert ticket as bullet list (single item)
        if (entity.entityType === 'ticket' && insertTicketListRef.current) {
          insertTicketListRef.current({
            id: entity.id,
            number: entity.number,
            title: entity.title,
            showTitle: true, // Always show title
          });
        }
      } catch (error) {
        console.error('Failed to parse dropped entity:', error);
      }
    }, []);

    useImperativeHandle(ref, () => ({
      clear: () => {
        clearPluginRef.current?.clear();
      },
      setValue: (newValue: string) => {
        setValue(newValue);
        if (setValueRef.current) {
          setValueRef.current(newValue);
        }
      },
      focus: () => {
        focusPluginRef.current?.focus();
      },
    }));

    return (
      <div>
        <LexicalComposer initialConfig={editorConfig}>
          <TicketListInsertProvider ref={insertTicketListRef}>
            <div
              className="editor-container w-full"
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <ToolbarPlugin
                isMarkdownMode={isMarkdownMode}
                onToggleMarkdownMode={handleToggleMarkdownMode}
                focusPluginRef={focusPluginRef}
                markdownTextareaRef={markdownTextareaRef}
              />
              <div
                className={cn(
                  'editor-inner rounded-b-md px-3 py-2 transition-colors',
                  isDragOver &&
                    'bg-green-50/30 ring-2 ring-green-500 ring-offset-2',
                )}
              >
                {isMarkdownMode ? (
                  <Textarea
                    ref={markdownTextareaRef}
                    disabled={disabled}
                    onBlur={onBlur}
                    value={value}
                    onKeyDown={onKeyDown}
                    onChange={(e) => handleChange(e.target.value)}
                    placeholder="Enter markdown here..."
                    className="min-h-3 resize-none border-none p-0 font-mono text-base shadow-none focus:ring-0
                      focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 md:text-sm"
                  />
                ) : (
                  <>
                    <div className="relative">
                      <RichTextPlugin
                        contentEditable={
                          <ContentEditable
                            disabled={disabled}
                            onBlur={onBlur}
                            className="editor-input relative block border-0 text-base outline-none focus:outline-none
                              focus-visible:outline-none md:text-sm"
                            aria-placeholder={placeholder}
                            placeholder={
                              <div
                                className="editor-placeholder text-muted-foreground pointer-events-none absolute top-0
                                  left-0 inline-block overflow-hidden text-base text-ellipsis whitespace-nowrap
                                  select-none md:text-sm"
                              >
                                {placeholder}
                              </div>
                            }
                          />
                        }
                        ErrorBoundary={LexicalErrorBoundary}
                      />
                    </div>
                    <HistoryPlugin />
                    <ListPlugin />
                    <ListIndentPlugin />
                    <CheckListPlugin />
                    <LinkPlugin />
                    <MarkdownImportPlugin
                      markdown={value}
                      setValueRef={setValueRef}
                    />
                    <MarkdownExportPlugin onMarkdownChange={handleChange} />
                    <MentionTypeaheadPlugin />
                    <TicketTypeaheadPlugin />
                    <PastePlugin onPasteFiles={onPasteFiles} />
                    <ClearPlugin ref={clearPluginRef} />
                    <FocusPlugin ref={focusPluginRef} />
                    <KeydownPlugin onKeyDown={onKeyDown} />
                  </>
                )}
              </div>
            </div>
          </TicketListInsertProvider>
        </LexicalComposer>
      </div>
    );
  },
);

Editor.displayName = 'Editor';
